import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import { useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import UsersApi from "../../../api/UsersApi";
import { appConstants } from "../../../config/app-constants";
import { EToastvariant, IUserChangePwdForm } from "../../../config/interfaces";
import { useAppDispatch } from "../../../redux/Store";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import FASvgIcon from "../../FASvgIcon";
import ScrollToFirstError from "../../ScrollToFirstError";
import PasswordHintOverlay from "../../overlay/PasswordHintOverlay";
import { ButtonSpinner } from "../../placeholders";

const initialValues: IUserChangePwdForm = {
  newPassword: "",
  confirmNewPassword: ""
};

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .trim()
    .matches(appConstants.PASSWORD_REGEX, "Invalid Password")
    .required("Password is required"),
  confirmNewPassword: Yup.string()
    .trim()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Repeat new password is required")
});

type Props = {
  pageName?: string;
};

const AdminUserCP: React.FC<Props> = (props: Props) => {
  const { pageName } = props;
  const { uUId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const pwdOverlayTargetRef = useRef<HTMLInputElement | null>(null);

  const [showHintOverlay, setShowHintOverlay] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmNewPwd, setShowConfirmNewPwd] = useState<boolean>(false);

  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleShowConfirmNewPwd = () => {
    setShowConfirmNewPwd(!showConfirmNewPwd);
  };

  const handleChangePwdFormSubmit = async (
    values: IUserChangePwdForm,
    { setSubmitting, resetForm }: FormikHelpers<IUserChangePwdForm>
  ) => {
    try {
      if (!uUId) {
        return;
      }
      setSubmitting(true);
      let _uUId = uUId;
      const _values = {
        newPassword: values?.newPassword
      };
      const adminUsersApi = UsersApi.getInstance();
      await adminUsersApi.userChangePassword(_uUId, _values);
      resetForm();
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Updated",
          message: "Password updated successfully.",
          variant: EToastvariant.Success
        })
      );
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="form-card">
        <Card.Body>
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleChangePwdFormSubmit}
          >
            {({
              handleBlur,
              handleChange,
              handleSubmit,
              validateForm,
              values,
              errors,
              touched,
              isValid,
              submitCount,
              isSubmitting,
              setTouched
            }) => (
              <>
                <Form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                  <Row className="align-items-center">
                    <Col sm={4}>
                      <Form.Label className="required-field inline-label">New Password</Form.Label>
                    </Col>
                    <Col>
                      <Form.Group className="form-group" controlId="newPassword">
                        <div className="field-has-icon" ref={pwdOverlayTargetRef}>
                          <Form.Control
                            type={showNewPassword ? "text" : "password"}
                            name="newPassword"
                            isInvalid={!!errors.newPassword && !!touched.newPassword}
                            value={values.newPassword}
                            placeholder="***********"
                            onChange={handleChange}
                            onFocus={() => {
                              setShowHintOverlay(true);
                            }}
                            onBlur={(e) => {
                              handleBlur(e);
                              setShowHintOverlay(false);
                            }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.newPassword}
                          </Form.Control.Feedback>
                          <Button
                            variant="btn"
                            className="field-icon"
                            onClick={toggleShowNewPassword}
                          >
                            <FASvgIcon icon={showNewPassword ? faEye : faEyeSlash} />
                          </Button>
                        </div>
                        <PasswordHintOverlay
                          show={showHintOverlay}
                          targetRef={pwdOverlayTargetRef}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="align-items-center">
                    <Col sm={4}>
                      <Form.Label className="required-field inline-label">
                        Repeat New Password
                      </Form.Label>
                    </Col>
                    <Col>
                      <Form.Group className="form-group" controlId="confirmNewPassword">
                        <div className="field-has-icon">
                          <Form.Control
                            type={showConfirmNewPwd ? "text" : "password"}
                            name="confirmNewPassword"
                            onBlur={handleBlur}
                            isInvalid={!!errors.confirmNewPassword && !!touched.confirmNewPassword}
                            value={values.confirmNewPassword}
                            placeholder="***********"
                            onChange={handleChange}
                            autoComplete="confirmNewPassword"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmNewPassword}
                          </Form.Control.Feedback>
                          <Button
                            variant="btn"
                            className="field-icon"
                            onClick={toggleShowConfirmNewPwd}
                          >
                            <FASvgIcon icon={showConfirmNewPwd ? faEye : faEyeSlash} />
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="form-btns-wrap">
                    <Button
                      size="lg"
                      variant="outline-greyDark"
                      className="btn-w-120"
                      onClick={() => {
                        uUId ? navigate(`/${pageName}`) : navigate(-1);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="lg"
                      variant="primary"
                      className="btn-w-160"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <ButtonSpinner /> : "Save"}
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};

export default AdminUserCP;
